import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Form,
  Modal as AntModal,
  Select,
  DatePicker,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

function PatientDetails({ patient, onClose, refetchPatients, hideStatus = false }) {
  const { t } = useTranslation("Patients");
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const { token } = useToken();
  const [caregivers, setCaregivers] = useState([]);

  useEffect(() => {
    if (patient) {
      setValue("firstname", patient.firstname);
      setValue("lastname", patient.lastname);
      setValue("email", patient.email);
      setValue("phoneNumber", patient.phoneNumber);
      setValue("status", patient.status);
      setValue("numberOfPrograms", patient.numberOfPrograms);
      setValue("numberOfCaregivers", patient.numberOfCaregivers);
      setValue("birthday", patient.birthday ? dayjs(patient.birthday) : null);
      setValue("weight", patient.weight);
      setValue("weightUnit", patient.weightUnit);
      setValue("otherinfo", patient.otherinfo);
      setValue("height", patient.height);
      setValue("gender", patient.gender);

    }
  }, [patient, setValue]);

  // Charger les caregivers du patient
// APRES : toujours fetch
// charge les caregivers d'un patient et pré-remplit le form
useEffect(() => {
  if (!patient?.id) return;

  const fetchCaregivers = async () => {
    try {
      const { data } = await axios.get(
        `${Constants.SERVER_URL}/patient-caregivers/${patient.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // l’API renvoie { caregivers: Caregiver[] }
      const list = Array.isArray(data?.caregivers) ? data.caregivers : [];
      setCaregivers(list);

      // pré-remplir les champs RHF avec la notation à points
      list.forEach((c, i) => {
        setValue(`caregivers.${i}.id`, c.id);
        setValue(`caregivers.${i}.firstname`, c.firstname);
        setValue(`caregivers.${i}.lastname`, c.lastname);
        setValue(`caregivers.${i}.email`, c.email);
        setValue(`caregivers.${i}.phoneNumber`, c.phoneNumber);
        setValue(`caregivers.${i}.relationship`, c.relationship);
      });
    } catch (err) {
  AntModal.error({
    content:
      t(`Backend:${err?.response?.data?.message}`) ||
      t("fetch_error_caregiver"),
    okText: t("button_close"),
    centered: true,
  });
}

  };

  fetchCaregivers();
}, [patient?.id, token, t, setValue]);



  const onSubmit = (data) => {
    const updatedCaregivers = getValues("caregivers");
    const patientData = {
      ...data,
      weight: Number(data.weight),
      birthday: data.birthday ? data.birthday.format("YYYY-MM-DD") : null,
      role: "patient",
      caregivers: updatedCaregivers || [],
      height: data.height ? Number(data.height) : null,
      gender: data.gender || null,

    };

    axios
      .put(
        `${Constants.SERVER_URL}/update-patient-with-caregivers/${patient.id}`,
        patientData,
        {
          headers: { Authorization: "Bearer " + token },
        }
      )
      .then(() => {
        refetchPatients();
        AntModal.success({
          content: t("success_patient_update"),
          okText: t("button_close"),
          centered: true,
          onOk: () => {
            onClose();
          },
        });
      })
      .catch((err) => {
        const errorMessage =
          t(`Backend:${err.response?.data?.message}`) ||
          t("error_patient_update");
        AntModal.error({
          content: errorMessage,
          okText: t("button_close"),
          centered: true,
        });
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("label_first_name")}
                required
                validateStatus={errors.firstname ? "error" : ""}
                help={errors.firstname?.message}
              >
                <Controller
                  name="firstname"
                  control={control}
                  rules={{
                    required: t("error_first_name_required"),
                    minLength: {
                      value: 2,
                      message: t("error_last_name_min_length"),
                    },
                  }}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("label_last_name")}
                required
                validateStatus={errors.lastname ? "error" : ""}
                help={errors.lastname?.message}
              >
                <Controller
                  name="lastname"
                  control={control}
                  rules={{
                    required: t("error_last_name_required"),
                    minLength: {
                      value: 2,
                      message: t("error_last_name_min_length"),
                    },
                  }}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("label_email")}
                required
                validateStatus={errors.email ? "error" : ""}
                help={errors.email?.message}
              >
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: t("error_email_required"),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t("error_email_invalid"),
                    },
                  }}
                  render={({ field }) => <Input type="email" {...field} />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("label_phone_number")}
                required
                validateStatus={errors.phoneNumber ? "error" : ""}
                help={errors.phoneNumber?.message}
              >
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: t("error_phone_number_required"),
                    pattern: {
                      value: /^[0-9+\s-]{8,}$/,
                      message: t("error_phone_number_invalid"),
                    },
                  }}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={hideStatus ? 24 : 12}>
              <Form.Item
                label={t("label_programs_quantity")}
                required
                validateStatus={errors.numberOfPrograms ? "error" : ""}
                help={errors.numberOfPrograms?.message}
              >
                <Controller
                  name="numberOfPrograms"
                  control={control}
                  rules={{
                    required: t("error_programs_quantity_required"),
                    min: {
                      value: 0,
                      message: t("error_programs_quantity_invalid"),
                    },
                  }}
                  render={({ field }) => <Input type="number" {...field} />}
                />
              </Form.Item>
            </Col>
            {!hideStatus ? (
              <Col span={12}>
                <Form.Item
                  label={t("label_status")}
                  required
                  validateStatus={errors.status ? "error" : ""}
                  help={errors.status?.message}
                >
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: t("error_status_required") }}
                    render={({ field }) => (
                      <Select {...field}>
                        <Select.Option value="active">
                          {t("option_active")}
                        </Select.Option>
                        <Select.Option value="paused">
                          {t("option_paused")}
                        </Select.Option>
                        <Select.Option value="waiting">
                          {t("option_waiting")}
                        </Select.Option>
                        <Select.Option value="completed">
                          {t("option_completed")}
                        </Select.Option>
                        <Select.Option value="abort">
                          {t("option_abort")}
                        </Select.Option>
                      </Select>
                    )}
                  />
                </Form.Item>
              </Col>
            ) : null}
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("label_weight")}
                required
                validateStatus={errors.weight ? "error" : ""}
                help={errors.weight?.message}
              >
                <Controller
                  name="weight"
                  control={control}
                  rules={{
                    required: t("error_weight_required"),
                    pattern: {
                      value: /^\d{1,3}(\.\d{1})?$/, // Nombre entre 0 et 999.9
                      message: t("error_weight_invalid"),
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => <Input type="number" {...field} />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("label_weight_unit")}
                required
                validateStatus={errors.weightUnit ? "error" : ""}
                help={errors.weight?.message}
              >
                <Controller
                  name="weightUnit"
                  control={control}
                  defaultValue="kg"
                  render={({ field }) => (
                    <Select {...field} style={{ width: "100%" }}>
                      <Select.Option value="kg">{t("option_kg")}</Select.Option>
                      <Select.Option value="lbs">
                        {t("option_lbs")}
                      </Select.Option>
                    </Select>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t("label_birthday")}
                required
                validateStatus={errors.birthday ? "error" : ""}
                help={errors.birthday?.message}
              >
                <Controller
                  name="birthday"
                  control={control}
                  rules={{
                    required: t("error_birthday_required"),
                    pattern: {
                      value: /^\d{1,3}(\.\d{1})?$/, // Nombre entre 0 et 999.9
                      message: t("error_birthday_invalid"),
                    },
                  }}
                  defaultValue={null}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      format="YYYY-MM-DD"
                      onChange={(date) => field.onChange(date)}
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("label_additionnal_information")}>
                <Controller
                  name="otherinfo"
                  control={control}
                  render={({ field }) => (
                    <Input.TextArea
                      {...field}
                      placeholder={t("placeholder_additionnal_information")}
                      rows={4}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t("label_height")}>
                <Controller
                  name="height"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\d{1,3}(\.\d{1,2})?$/,
                      message: t("error_height_invalid"),
                    },
                  }}
                  render={({ field }) => (
                    <Input {...field} placeholder={t("placeholder_height_cm")} />
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t("label_gender")}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} allowClear placeholder={t("placeholder_gender")}>
                      <Select.Option value="male">{t("option_male")}</Select.Option>
                      <Select.Option value="female">{t("option_female")}</Select.Option>
                      <Select.Option value="other">{t("option_other")}</Select.Option>
                    </Select>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Caregivers */}
         {/* Caregivers */}
{Array.isArray(caregivers) && caregivers.length > 0 ? (
  <>
    <h3>{t("title_caregiver_list")}</h3>

    {caregivers.map((caregiver, index) => (
      <Row gutter={16} key={caregiver?.id ?? index}>
        <Col span={24}>
          <h4>
            {t("label_caregiver")} {index + 1}
          </h4>
        </Col>

        <Col span={12}>
          <Form.Item
            label={t("label_first_name")}
            required
            validateStatus={errors?.caregivers?.[index]?.firstname ? "error" : ""}
            help={errors?.caregivers?.[index]?.firstname?.message}
          >
            <Controller
              name={`caregivers.${index}.firstname`}
              control={control}
              rules={{
                required: t("error_first_name_required"),
                minLength: { value: 2, message: t("error_first_name_min_length") },
              }}
              render={({ field }) => <Input {...field} />}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label={t("label_last_name")}
            required
            validateStatus={errors?.caregivers?.[index]?.lastname ? "error" : ""}
            help={errors?.caregivers?.[index]?.lastname?.message}
          >
            <Controller
              name={`caregivers.${index}.lastname`}
              control={control}
              rules={{
                required: t("error_last_name_required"),
                minLength: { value: 2, message: t("error_last_name_min_length") },
              }}
              render={({ field }) => <Input {...field} />}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label={t("label_email")}
            required
            validateStatus={errors?.caregivers?.[index]?.email ? "error" : ""}
            help={errors?.caregivers?.[index]?.email?.message}
          >
            <Controller
              name={`caregivers.${index}.email`}
              control={control}
              rules={{
                required: t("error_email_required"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("error_email_invalid"),
                },
              }}
              render={({ field }) => <Input type="email" {...field} />}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label={t("label_phone_number")}
            required
            validateStatus={errors?.caregivers?.[index]?.phoneNumber ? "error" : ""}
            help={errors?.caregivers?.[index]?.phoneNumber?.message}
          >
            <Controller
              name={`caregivers.${index}.phoneNumber`}
              control={control}
              rules={{
                required: t("error_phone_number_required"),
                pattern: {
                  value: /^[0-9+\s-]{8,}$/,
                  message: t("error_phone_number_invalid"),
                },
              }}
              render={({ field }) => <Input {...field} />}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t("label_relationship")}
            required
            validateStatus={errors?.caregivers?.[index]?.relationship ? "error" : ""}
            help={errors?.caregivers?.[index]?.relationship?.message}
          >
            <Controller
              name={`caregivers.${index}.relationship`}
              control={control}
              rules={{ required: t("placeholder_relationship") }}
              render={({ field }) => (
                <Select {...field}>
                  <Select.Option value="parent">{t("option_parent")}</Select.Option>
                  <Select.Option value="sibling">{t("option_sibling")}</Select.Option>
                  <Select.Option value="friend">{t("option_friend")}</Select.Option>
                  <Select.Option value="volunteer_companion">
                    {t("option_volunteer_companion")}
                  </Select.Option>
                  <Select.Option value="professional_caregiver">
                    {t("option_professional_caregiver")}
                  </Select.Option>
                  <Select.Option value="colleague">{t("option_colleague")}</Select.Option>
                  <Select.Option value="spouse">{t("option_spouse")}</Select.Option>
                  <Select.Option value="child">{t("Patients:relation_child")}</Select.Option>
                  <Select.Option value="community_member">
                    {t("option_community_member")}
                  </Select.Option>
                  <Select.Option value="nephew_niece">
                    {t("option_nephew_niece")}
                  </Select.Option>
                  <Select.Option value="grandchild">{t("option_grandchild")}</Select.Option>
                  <Select.Option value="neighbor">{t("option_neighbor")}</Select.Option>
                  <Select.Option value="other">{t("option_other")}</Select.Option>
                </Select>
              )}
            />
          </Form.Item>
        </Col>
      </Row>
    ))}
  </>
) : null}
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {t("button_patient_update")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

PatientDetails.propTypes = {
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchPatients: PropTypes.func.isRequired,
  hideStatus: PropTypes.bool,
};

export default PatientDetails;
